import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowRight, Calendar, Car, CircleNotch, Clock, CurrencyEur, MapPin } from '@phosphor-icons/react';
import API_URL from '@/config';
import VehicleFeatureBadges from '@/components/VehicleFeatureBadges';
import {
  findPriceEstimateForCategory,
  getCategoryDisplayName,
  getVehicleCategoryImageUrl,
  getVehicleCategoryPresentation,
} from '@/utils/vehicleCategories';
import { buildEstimatePriceQuery, parseBookingError } from './newBookingUtils';
import { createCheckoutSession, saveBookingCheckoutDraft } from '@/utils/bookingCheckout';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import InteractiveMap from '@/components/InteractiveMap';
import useBookingRoute from '@/hooks/useBookingRoute';
import { bookingCoordinates } from '@/utils/bookingMap';

const NewBooking = () => {
  const enableDebugLogging = process.env.NODE_ENV !== 'production';
  const { lang = 'fr' } = useParams();
  const [date, setDate] = useState();
  const [time, setTime] = useState('');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [transferType, setTransferType] = useState('');
  const [dispositionHours, setDispositionHours] = useState('');
  const [passengerCount, setPassengerCount] = useState('1');
  const [babySeatCount, setBabySeatCount] = useState('0');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Vehicle & pricing
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [distance, setDistance] = useState('');
  const [duration, setDuration] = useState('');
  const [priceEstimates, setPriceEstimates] = useState([]);
  const [estimatingPrice, setEstimatingPrice] = useState(false);
  const onRouteChange = useCallback((route) => {
    setDistance(route ? String(route.distanceKm) : '');
    setDuration(route ? String(route.durationMinutes) : '');
    setPriceEstimates([]);
  }, []);
  const bookingRoute = useBookingRoute({ pickup, dropoff, setPickup, setDropoff, onRouteChange });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vehicle-categories`, {
        withCredentials: true
      });
      const safeCategories = Array.isArray(response.data) ? response.data : [];
      setCategories(safeCategories);
      if (enableDebugLogging) {
        console.info('Vehicle categories received:', safeCategories.map((category) => ({
          id: category.id,
          name: category.name,
          has_wifi: category.has_wifi,
          max_passengers: category.max_passengers,
          max_luggage: category.max_luggage
        })));
      }
    } catch (err) {
      setCategories([]);
      setError(parseBookingError(err, 'Erreur lors du chargement des catégories de véhicules'));
    }
  }, [enableDebugLogging]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const estimatePrice = useCallback(async (signal) => {
    const queryString = buildEstimatePriceQuery({
      transferType,
      distance,
      duration,
      dispositionHours,
    });
    if (!queryString) return;
    
    setEstimatingPrice(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/estimate-price?${queryString}`,
        {},
        { withCredentials: true, signal }
      );
      if (signal.aborted) return;
      setPriceEstimates(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (signal.aborted || axios.isCancel(err)) return;
      setPriceEstimates([]);
      setError(parseBookingError(err, 'Erreur lors du calcul des tarifs'));
    } finally {
      if (!signal.aborted) setEstimatingPrice(false);
    }
  }, [distance, dispositionHours, duration, transferType]);

  useEffect(() => {
    setPriceEstimates([]);
    const shouldEstimateDisposition = transferType === 'disposition' && dispositionHours && parseFloat(dispositionHours) > 0;
    const shouldEstimateDistance = transferType !== 'disposition' && distance && parseFloat(distance) > 0;

    if (shouldEstimateDisposition || shouldEstimateDistance) {
      const controller = new AbortController();
      setEstimatingPrice(true);
      const timer = setTimeout(() => estimatePrice(controller.signal), 500);
      return () => { clearTimeout(timer); controller.abort(); };
    } else {
      setPriceEstimates([]);
      setEstimatingPrice(false);
    }
  }, [distance, dispositionHours, duration, estimatePrice, transferType]);

  const timeSlots = Array.from({ length: 48 }, (_, i) => {
    const hours = String(Math.floor(i / 2)).padStart(2, '0');
    const minutes = i % 2 === 0 ? '00' : '30';
    return `${hours}:${minutes}`;
  });

  const getSelectedPrice = () => {
    if (!selectedCategory) return null;
    const category = categories.find((entry) => entry.id === selectedCategory);
    return findPriceEstimateForCategory(priceEstimates, selectedCategory, category?.name);
  };

  const getEstimateRateLabel = (estimate) => {
    if (estimate.final_price === null || estimate.final_price === undefined) {
      return 'Tarif indisponible';
    }
    return estimate.rate_label
      || (estimate.pricing_basis === 'hourly'
        ? `Tarif ${estimate.disposition_hours}h`
        : `${estimate.price_per_km.toFixed(2)}€/km`);
  };

  const formatReservedHours = (hoursValue) => {
    const parsedHours = parseFloat(hoursValue);
    if (!Number.isFinite(parsedHours)) return '';
    return `${parsedHours.toFixed(parsedHours % 1 === 0 ? 0 : 1)} h`;
  };

  const getCategoryMeta = (category) => {
    const parseNumberOrNull = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    if (!category) return { hasWifi: false, passengers: null, luggage: null };
    return {
      hasWifi: category.has_wifi === true,
      passengers: parseNumberOrNull(category.max_passengers),
      luggage: parseNumberOrNull(category.max_luggage)
    };
  };

  const getDisplayMeta = (categoryName, categoryMeta) => {
    const preset = getVehicleCategoryPresentation(categoryName);
    if (preset) {
      return {
        displayName: preset.displayName,
        passengers: preset.passengers,
        luggage: preset.luggage,
        hasWifi: preset.hasWifi,
        image: preset.image,
        order: preset.order,
      };
    }
    return {
      displayName: getCategoryDisplayName(categoryName),
      passengers: categoryMeta.passengers ?? 4,
      luggage: categoryMeta.luggage ?? 2,
      hasWifi: categoryMeta.hasWifi,
      image: null,
      order: 99,
    };
  };

  useEffect(() => {
    if (!enableDebugLogging || priceEstimates.length === 0) return;

    const badgeDebug = priceEstimates.map((estimate) => {
      const category = categories.find(c => c.id === estimate.category_id || c.name === estimate.category_name);
      return {
        estimate_category_id: estimate.category_id,
        estimate_category_name: estimate.category_name,
        category_found: Boolean(category),
        has_wifi: category?.has_wifi ?? null,
        max_passengers: category?.max_passengers ?? null,
        max_luggage: category?.max_luggage ?? null
      };
    });

    console.info('Vehicle card badge data:', badgeDebug);
  }, [categories, priceEstimates, enableDebugLogging]);

  const sortedCategories = [...categories].sort((a, b) => {
    const aPresentation = getVehicleCategoryPresentation(a.name);
    const bPresentation = getVehicleCategoryPresentation(b.name);
    return (aPresentation?.order ?? 99) - (bPresentation?.order ?? 99);
  });
  const passengerLimit = Number(categories.find((category) => category.id === selectedCategory)?.max_passengers) || 7;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!date || !time || !pickup || !dropoff || !transferType) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!selectedCategory) {
      setError('Veuillez choisir une gamme de véhicule');
      return;
    }
    if (!bookingRoute.geocoded) {
      setError('Choisissez et géocodez les adresses de départ et d’arrivée avant de réserver.');
      return;
    }

    if (transferType === 'disposition' && !dispositionHours) {
      setError('Veuillez indiquer le nombre d\'heures de mise à disposition');
      return;
    }
    if (bookingRoute.loading || estimatingPrice
      || !Number.isFinite(getSelectedPrice()?.final_price)) {
      setError('Veuillez attendre le calcul du tarif. Choisissez les adresses proposées ou renseignez une distance valide.');
      return;
    }

    setLoading(true);
    const selectedPrice = getSelectedPrice();

    try {
      const payload = {
        ...bookingCoordinates(bookingRoute.addresses),
        pickup_address: pickup,
        dropoff_address: dropoff,
        pickup_date: format(date, 'dd/MM/yyyy', { locale: fr }),
        pickup_time: time,
        transfer_type: transferType,
        vehicle_category_id: selectedCategory,
        distance_km: transferType === 'disposition' ? null : (distance ? parseFloat(distance) : null),
        duration_minutes: transferType === 'disposition' ? null : (duration ? parseFloat(duration) : null),
        estimated_price: selectedPrice?.final_price || null,
        notes: notes,
        disposition_hours: dispositionHours ? parseFloat(dispositionHours) : null,
        passenger_count: Number(passengerCount),
        baby_seat_count: Number(babySeatCount),
        success_path: `/${lang}/booking/confirmation`,
        cancel_path: `/${lang}/booking/cancel`,
      };

      saveBookingCheckoutDraft({
        date: date ? date.toISOString() : null,
        pickup,
        dropoff,
        time,
        transferType,
        selectedCategory,
        dispositionHours,
        distanceKm: distance,
        durationMinutes: duration,
        mapAddresses: bookingRoute.addresses,
        step: 3,
        autoPayAfterAuth: false,
        checkoutPayload: payload,
      });

      const checkout = await createCheckoutSession(payload);
      if (!checkout?.checkout_url) {
        throw new Error('URL Stripe introuvable');
      }
      window.location.href = checkout.checkout_url;
    } catch (err) {
      setError(parseBookingError(err, 'Erreur lors de la réservation'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] text-white min-h-full">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Form */}
        <div className="glass rounded-xl p-5 sm:p-6 md:p-8 lg:col-span-2" data-testid="new-booking-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date & Time */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left bg-[#1E1E1E] border-white/10" data-testid="date-picker">
                      <Calendar size={18} className="mr-2 text-[#D4AF37]" />
                      {date ? format(date, 'dd/MM/yyyy', { locale: fr }) : 'Choisir'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#1E1E1E] border-white/10">
                    <CalendarComponent mode="single" selected={date} onSelect={setDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Heure *</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="time-select">
                    <Clock size={18} className="mr-2 text-[#D4AF37]" />
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E1E1E] border-white/10 max-h-60">
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Addresses */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pickup" className="text-[#A1A1AA]">Adresse de depart *</Label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-3 top-3 text-[#D4AF37]" />
                  <AddressAutocomplete id="pickup" value={pickup}
                    onChange={(value) => bookingRoute.changeAddress('pickup', value)}
                    onSelect={(address) => bookingRoute.selectAddress('pickup', address)}
                    onFocus={() => bookingRoute.setActiveField('pickup')}
                    selectedAddress={bookingRoute.addresses.pickup}
                    placeholder="Entrez l'adresse" className="pl-10 bg-[#1E1E1E] border-white/10" data-testid="pickup-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dropoff" className="text-[#A1A1AA]">Adresse d'arrivee *</Label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-3 top-3 text-[#F3D67A]" />
                  <AddressAutocomplete id="dropoff" value={dropoff}
                    onChange={(value) => bookingRoute.changeAddress('dropoff', value)}
                    onSelect={(address) => bookingRoute.selectAddress('dropoff', address)}
                    onFocus={() => bookingRoute.setActiveField('dropoff')}
                    selectedAddress={bookingRoute.addresses.dropoff}
                    placeholder="Entrez l'adresse" className="pl-10 bg-[#1E1E1E] border-white/10" data-testid="dropoff-input" />
                </div>
              </div>
            </div>

            {/* Transfer Type */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Type de transfert *</Label>
              <Select value={transferType} onValueChange={setTransferType}>
                <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="transfer-type">
                  <SelectValue placeholder="Choisir le type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  <SelectItem value="simple">Sens Unique</SelectItem>
                  <SelectItem value="retour">Aller-Retour</SelectItem>
                  <SelectItem value="disposition">Mise a Disposition</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {transferType !== 'disposition' && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Distance estimee (km)</Label>
                  <Input 
                    type="number" 
                    step="0.1"
                    min="0"
                    value={distance} 
                    readOnly={Boolean(bookingRoute.route) || bookingRoute.loading}
                    onChange={(e) => setDistance(e.target.value)} 
                    placeholder="Ex: 15.5" 
                    className="bg-[#1E1E1E] border-white/10" 
                    data-testid="distance-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Duree estimee (min)</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={duration} 
                    readOnly={Boolean(bookingRoute.route) || bookingRoute.loading}
                    onChange={(e) => setDuration(e.target.value)} 
                    placeholder="Ex: 30" 
                    className="bg-[#1E1E1E] border-white/10"
                    data-testid="duration-input"
                  />
                </div>
              </div>
            )}

            {/* Disposition hours — shown and required when transferType === 'disposition' */}
            {transferType === 'disposition' && (
              <div className="space-y-3 rounded-xl border border-[#D4AF37]/20 bg-[#141414] p-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Nombre d'heures <span className="text-red-400">*</span></Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.5"
                    value={dispositionHours}
                    onChange={(e) => setDispositionHours(e.target.value)}
                    placeholder="Ex: 4 (peut dépasser 24h)"
                    className="bg-[#1E1E1E] border-white/10"
                    data-testid="disposition-hours-input"
                  />
                </div>
                <p className="text-xs text-[#A1A1AA]">
                  Le tarif de mise à disposition est calculé sur la base du montant horaire × nombre d'heures, pas selon les kilomètres.
                </p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Nombre de passagers</Label>
                <Select
                  value={passengerCount}
                  onValueChange={(value) => {
                    setPassengerCount(value);
                    if (Number(babySeatCount) > Number(value)) setBabySeatCount(value);
                  }}
                >
                  <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="passenger-count-select">
                    <SelectValue placeholder="Choisir le nombre de passagers" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E1E1E] border-white/10">
                    {Array.from({ length: passengerLimit }, (_, index) => index + 1).map((count) => (
                      <SelectItem key={count} value={String(count)}>{count}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Sièges bébé nécessaires</Label>
                <Select
                  value={babySeatCount}
                  onValueChange={setBabySeatCount}
                >
                  <SelectTrigger className="bg-[#1E1E1E] border-white/10" data-testid="baby-seat-count-select">
                    <SelectValue placeholder="Choisir le nombre de sièges bébé" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E1E1E] border-white/10">
                    {Array.from({ length: Number(passengerCount) + 1 }, (_, count) => count).map((count) => (
                      <SelectItem key={count} value={String(count)}>{count}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vehicle Category Selection */}
            {sortedCategories.length > 0 && (
              <div className="space-y-3">
                <Label className="text-[#A1A1AA]">Choisir votre véhicule</Label>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="vehicle-selection">
                  {sortedCategories.map((category) => {
                    const estimate = findPriceEstimateForCategory(priceEstimates, category.id, category.name);
                    const categoryMeta = getCategoryMeta(category);
                    const displayMeta = getDisplayMeta(category.name, categoryMeta);
                    const isSelected = selectedCategory === category.id;
                    const hasPrice = Number.isFinite(Number(estimate?.final_price));
                    const minFare = Number(category?.min_fare);
                    return (
                      <div 
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          const maxPassengers = Number(category.max_passengers) || 7;
                          if (Number(passengerCount) > maxPassengers) {
                            setPassengerCount(String(maxPassengers));
                            setBabySeatCount(String(Math.min(Number(babySeatCount), maxPassengers)));
                          }
                        }}
                        className={`overflow-hidden rounded-2xl transition-all ${
                          'cursor-pointer'
                        } ${
                          isSelected 
                            ? 'bg-[#D4AF37]/20 border-2 border-[#D4AF37]' 
                            : 'bg-[#1E1E1E] border-2 border-transparent hover:border-white/20'
                        }`}
                        data-testid={`vehicle-${category.id}`}
                      >
                        {getVehicleCategoryImageUrl(category.name, category?.image_url) ? (
                          <img
                            src={getVehicleCategoryImageUrl(category.name, category?.image_url)}
                            alt={displayMeta.displayName}
                            className="h-36 w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-36 items-center justify-center bg-[#111111]">
                            <Car size={36} className="text-[#D4AF37]" />
                          </div>
                        )}
                        <div className="space-y-3 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-bold text-base">{displayMeta.displayName}</p>
                              <p className="text-xs text-[#A1A1AA]">
                                {estimate ? getEstimateRateLabel(estimate) : 'Sélectionnez la distance ou la durée pour calculer le tarif'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-[#D4AF37]">
                                {hasPrice ? `${Number(estimate.final_price).toFixed(2)}€` : Number.isFinite(minFare) ? `dès ${minFare.toFixed(2)}€` : '—'}
                              </p>
                              {hasPrice && Number(estimate.final_price) === Number(estimate.min_fare) && estimate.pricing_basis !== 'hourly' && (
                                <p className="text-xs text-[#A1A1AA]">Tarif min.</p>
                              )}
                            </div>
                          </div>
                          <VehicleFeatureBadges
                            passengers={displayMeta.passengers}
                            luggage={displayMeta.luggage}
                            hasWifi={displayMeta.hasWifi}
                            iconSize={14}
                            itemClassName="text-xs text-[#D4AF37]"
                            showWifiLabel={false}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Notes (optionnel)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Instructions speciales..." className="bg-[#1E1E1E] border-white/10 min-h-[80px]" data-testid="notes-input" />
            </div>

            <Button type="submit" disabled={loading || bookingRoute.loading || estimatingPrice} className="w-full bg-[#D4AF37] hover:bg-[#F0C74A] text-[#0A0A0A] font-semibold py-6" data-testid="submit-booking">
              {loading ? <CircleNotch size={20} className="animate-spin" /> : <>Confirmer <ArrowRight size={20} className="ml-2" /></>}
            </Button>
          </form>
        </div>

        {/* Price Summary Sidebar */}
        <div className="glass h-fit rounded-xl p-5 sm:p-6 lg:sticky lg:top-24">
          <div className="mb-6">
            <InteractiveMap
              pickup={bookingRoute.addresses.pickup}
              dropoff={bookingRoute.addresses.dropoff}
              route={bookingRoute.route}
              activeField={bookingRoute.activeField}
              onActiveFieldChange={bookingRoute.setActiveField}
              onSelectPosition={bookingRoute.selectMapPosition}
              loading={bookingRoute.loading}
              error={bookingRoute.error}
            />
          </div>
          <h3 className="text-lg font-bold font-['Cormorant_Garamond'] mb-4 flex items-center gap-2">
            <CurrencyEur size={24} className="text-[#D4AF37]" />
            Estimation tarifaire
          </h3>

          {transferType === 'disposition' && !dispositionHours ? (
            <p className="text-[#A1A1AA] text-sm">Indiquez le nombre d'heures pour afficher les tarifs de mise à disposition.</p>
          ) : transferType !== 'disposition' && !distance ? (
            <p className="text-[#A1A1AA] text-sm">Choisissez les adresses pour calculer le trajet et les tarifs. Distance manuelle possible si le routage est indisponible.</p>
          ) : estimatingPrice ? (
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <CircleNotch size={20} className="animate-spin" />
              Calcul en cours...
            </div>
          ) : sortedCategories.length === 0 ? (
            <p className="text-[#A1A1AA] text-sm">Aucune categorie disponible.</p>
          ) : (
            <div className="space-y-4">
              <div className="bg-[#1E1E1E] rounded-lg p-4">
                <p className="text-sm text-[#A1A1AA]">{transferType === 'disposition' ? 'Durée réservée' : 'Distance'}</p>
                <p className="text-xl font-bold">
                  {transferType === 'disposition'
                    ? formatReservedHours(dispositionHours)
                    : `${parseFloat(distance).toFixed(1)} km`}
                </p>
              </div>

              {selectedCategory && getSelectedPrice() && (
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/50 rounded-lg p-4">
                  <p className="text-sm text-[#A1A1AA]">{transferType === 'disposition' ? 'Gamme sélectionnée' : 'Vehicule selectionne'}</p>
                  <p className="font-bold">{getCategoryDisplayName(getSelectedPrice().category_name)}</p>
                  <div className="mt-2 pt-2 border-t border-[#D4AF37]/30">
                    <p className="text-sm text-[#A1A1AA]">{transferType === 'disposition' ? 'Tarif mise à disposition' : 'Prix estime'}</p>
                    <p className="text-3xl font-bold text-[#D4AF37]">{getSelectedPrice().final_price.toFixed(2)}€</p>
                  </div>
                </div>
              )}

              <div className="text-xs text-[#A1A1AA]">
                * Prix indicatif. Le tarif final peut varier selon les conditions de circulation.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewBooking;
